import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Globe, MessageSquare, Send } from 'lucide-react';
import { agencyService, AvailableBrand, CreateBrandRequestPayload } from '@/services/agency.service';
import { toast } from 'react-hot-toast';

interface RequestBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestSent: () => void;
}

export function RequestBrandModal({ isOpen, onClose, onRequestSent }: RequestBrandModalProps) {
  const [availableBrands, setAvailableBrands] = useState<AvailableBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<AvailableBrand | null>(null);
  const [message, setMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBrands, setTotalBrands] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableBrands();
    }
  }, [isOpen]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchAvailableBrands(1, searchTerm);
      } else {
        fetchAvailableBrands(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchAvailableBrands = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      const response = await agencyService.getAvailableBrandsForRequest(page, 20, search);
      setAvailableBrands(response.brands);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalBrands(response.pagination.total);
    } catch (error) {
      console.error('Failed to fetch available brands:', error);
      toast.error('Failed to load available brands');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSelect = (brand: AvailableBrand) => {
    setSelectedBrand(brand);
    setMessage(''); // Reset message when selecting a new brand
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchAvailableBrands(page, searchTerm);
  };

  const handleSendRequest = async () => {
    if (!selectedBrand) return;

    try {
      setSendingRequest(true);
      const payload: CreateBrandRequestPayload = {
        brandDetailId: selectedBrand.id,
        message: message.trim() || undefined,
      };

      await agencyService.createBrandRequest(payload);
      
      toast.success(`Request sent to ${selectedBrand.companyName}!`);
      onRequestSent();
      onClose();
      
      // Reset state
      setSelectedBrand(null);
      setMessage('');
    } catch (error) {
      console.error('Failed to send request:', error);
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };



  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-4xl max-h-[80vh] overflow-y-auto bg-card rounded-lg shadow-xl">
              <div className="p-6">
                <div className="mb-6">
                  <Dialog.Title className="text-2xl font-bold">Request to Become Brand Partner</Dialog.Title>
                  <p className="text-muted-foreground mt-2">
                    Select a brand you'd like to represent and send them a request to become their agent.
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search brands by name, description, or niche..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>

                  {/* Results Info */}
                  <div className="text-sm text-muted-foreground">
                    Showing {availableBrands.length} of {totalBrands} brands
                  </div>

                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
                      <p className="mt-2 text-muted-foreground">Loading available brands...</p>
                    </div>
                  ) : (
                    <>
                      {/* Available Brands Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {availableBrands.map((brand: AvailableBrand) => (
                          <Card
                            key={brand.id}
                            className={`cursor-pointer transition-all hover:shadow-md ${
                              selectedBrand?.id === brand.id
                                ? 'ring-2 ring-brand-500 bg-brand-50'
                                : 'hover:border-brand-200'
                            }`}
                            onClick={() => handleBrandSelect(brand)}
                          >
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg flex items-center gap-2">
                                {brand.companyName}
                                {brand.website && (
                                  <Globe className="w-4 h-4 text-muted-foreground" />
                                )}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              {brand.description && (
                                <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                                  {brand.description}
                                </p>
                              )}
                              
                              {brand.website && (
                                <a
                                  href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-600 hover:text-brand-700 text-sm block mb-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {brand.website}
                                </a>
                              )}

                              <div className="flex flex-wrap gap-1">
                                {brand.niches.map((niche: { name: string }, index: number) => (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {niche.name}
                                  </Badge>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>

                      {availableBrands.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          {searchTerm ? (
                            <p>No brands found matching "{searchTerm}"</p>
                          ) : (
                            <p>No brands available for requests at the moment</p>
                          )}
                        </div>
                      )}

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex justify-center mt-6">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="px-3 py-2 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
                            >
                              Previous
                            </button>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-2 text-sm border rounded-md ${
                                  currentPage === page
                                    ? 'bg-brand-600 text-white border-brand-600'
                                    : 'border-border hover:bg-accent'
                                }`}
                              >
                                {page}
                              </button>
                            ))}
                            
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Request Form */}
                      {selectedBrand && (
                        <Card className="border-brand-200 bg-brand-50">
                          <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <MessageSquare className="w-5 h-5 text-brand-600" />
                              Request to represent {selectedBrand.companyName}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-2">
                                  Message (Optional)
                                </label>
                                <textarea
                                  placeholder="Tell the brand how you referred them to Reveshare."
                                  value={message}
                                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                                  rows={4}
                                  className="resize-none w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Explain how you referred them to Reveshare.
                                </p>
                              </div>

                              <div className="flex gap-3">
                                <Button
                                  onClick={handleSendRequest}
                                  disabled={sendingRequest}
                                  className="flex-1"
                                >
                                  {sendingRequest ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-4 h-4 mr-2" />
                                      Send Request
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => setSelectedBrand(null)}
                                  disabled={sendingRequest}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
